import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Configuration } from "@app/app.constants";
import { MailMessageModel } from "@app/mailmessages/models/mailMessageModel";
import { MailMessageService } from "@app/mailmessages/service/mailmessage.service";
import { CerrixEditorComponent } from "@app/shared/components/cerrix-editor/cerrix-editor.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { Observable } from "rxjs";

@Component({
    selector: "uc-mailmessages-detail",
    templateUrl: "./mailmessages-detail.component.html",
    styleUrls: ["./mailmessages-detail.component.scss"],
    standalone: false,
    providers: [
        provideTranslocoScope({
            scope: "mailmessages/pages/mailmessages/detail",
            alias: "mailmessagesDetail"
        })
    ]
})
export class MailMessageDetailComponent implements OnInit {
    private _mmService = inject(MailMessageService);
    private _route = inject(ActivatedRoute);
    private toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _transloco = inject(TranslocoService);

    @ViewChild("editor") editorComponent: CerrixEditorComponent;
    tab: TabModel;
    tabID: string;
    id: number;

    pageReady = false;
    showConditions = false;

    mailMessage: MailMessageModel;
    keywords: string[];
    orgs: CerrixTreeItem[];
    conditions: IdNameCombination[];
    module: ModuleType;

    form: FormGroup;
    isEmpty = false;

    organizationRequest: Observable<CerrixTreeItem[]>;
    conditionsRequest: Observable<IdNameCombination[]>;
    noOrganizationsSelectedDescription = Configuration.NoOrganizationsSelectedDescription;

    constructor() {
        this.id = this._route.snapshot.params.id;
    }
    ngOnInit() {
        this._mmService.getMailMessage(this.id).subscribe((mail) => {
            this.keywords = mail.Keywords;
            this.mailMessage = mail;
            this.module = mail.Module;
            this.showConditions = this.module === ModuleType.ORM; //only ORM has conditions for now, so we can hide the field for other modules
            if(this.showConditions) {
                this.conditionsRequest = this._mmService.getConditions(this.module);
            }

            this.initFormGroup(this.mailMessage);
            this.pageReady = true;
        });

        this.organizationRequest = this._mmService.getOrganizations();
        
    }

    initFormGroup(mail: MailMessageModel) {
        this.form = new FormGroup({
            subject: new FormControl(mail.Subject, [Validators.required]),
            OrganizationIdList: new FormControl(mail.OrganizationIdList),
            ConditionsIdList: new FormControl(mail.ConditionsIdList),
        });

        this.form.valueChanges.subscribe(() =>
            FormValidationHelper.mapToModel(this.form, this.mailMessage)
        );
    }

    onChange(value: string) {
        this.mailMessage.Message = value;
    }

    addKeyword(keyword: string) {
        this.editorComponent.insertText(" [" + keyword + "]");
    }

    save() {
        this.isEmpty = false;

        if (!this.mailMessage.OrganizationIdList) {
            this.mailMessage.OrganizationIdList = [];
        }

        this.mailMessage.OrganizationsCSV = this.mailMessage.OrganizationIdList.join(";");
        const messageEmpty = this.editorComponent.isEmpty();
        if (this.form.valid && !messageEmpty) {
            this._mmService.saveMailMessage(this.mailMessage).subscribe((_) => {
                this.toastr.success(
                    "",
                    this._transloco.translate("mailmessagesDetail.updateCompletedToast")
                );
                this._tabService.refresh(this._pages.MailMessages);
            });
        } else {
            FormValidationHelper.markAllAsTouched(this.form);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.form);
            let validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);

            if (messageEmpty) {
                this.isEmpty = true;
                validationMessage += this._transloco.translate(
                    "mailmessagesDetail.messageRequiredValidation"
                );
            }

            this.toastr.warning(
                validationMessage,
                this._transloco.translate("mailmessagesDetail.saveFailedToast"),
                { enableHtml: true }
            );
        }
    }
}
